import { db } from '../config/firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';

export interface StillActiveWordData {
  term: string;
  clicks: number;
  probability?: number;
  killedInClicks?: number;
}

export interface StillActiveWordsSummary {
  totalActiveWords: number;
  wordClickThreshold: number;
  activeWords: StillActiveWordData[];
}

interface AllWordResultsData {
  asin?: string;
  companyName?: string;
  wordResultEntries?: WordResultEntry[];
  lastUpdated?: any;
  source?: string;
}

interface WordResultEntry {
  term?: string | null;
  clicks?: number | string | null;
  sales?: number | string | null;
  stillActive?: string | null;
}

interface DispositionLedgerData {
  term?: string;
  percentProbability?: number;
  asin?: string;
  cst?: string;
  companyName?: string;
}

interface PMT2ProductData {
  'Final Farming WORD Max Clicks'?: number;
  [key: string]: any;
}

interface BrandNamesData {
  terms?: string[];
}

const isAllWordResultsData = (data: unknown): data is AllWordResultsData => {
  return typeof data === 'object' && data !== null && 'wordResultEntries' in data;
};

const isDispositionLedgerData = (data: unknown): data is DispositionLedgerData => {
  return typeof data === 'object' && data !== null;
};

const isPMT2ProductData = (data: unknown): data is PMT2ProductData => {
  return typeof data === 'object' && data !== null;
};

const isBrandNamesData = (data: unknown): data is BrandNamesData => {
  return typeof data === 'object' && data !== null && 'terms' in data;
};

const parseNumericValue = (value: any): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/,/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

export const fetchWordClickThreshold = async (companyName: string, asin: string): Promise<number> => {
  try {
    console.log(`Fetching Word Click Threshold for: ${companyName}/${asin}`);
    
    const productDocRef = doc(db, 'pmt2_data', companyName, 'products', asin);
    const productSnap = await getDoc(productDocRef);
    
    if (productSnap.exists()) {
      const data = productSnap.data();
      if (isPMT2ProductData(data) && typeof data['Final Farming WORD Max Clicks'] === 'number') {
        console.log(`Found Word Click Threshold: ${data['Final Farming WORD Max Clicks']}`);
        return data['Final Farming WORD Max Clicks'];
      }
    }
    
    console.warn(`No Word Click Threshold found for ${companyName}/${asin}, using default: 25`);
    return 25;
  } catch (error) {
    console.error('Error fetching Word Click Threshold:', error);
    return 25;
  }
};

export const fetchBrandNamesSet = async (companyName: string): Promise<Set<string>> => {
  try {
    console.log(`Fetching Brand Names for company: ${companyName}`);
    
    const brandNamesSet = new Set<string>();
    const dataDocRef = doc(db, 'pmt3_brand_names_to_neg_out', companyName, 'data', 'terms');
    const docSnap = await getDoc(dataDocRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (isBrandNamesData(data) && Array.isArray(data.terms)) {
        data.terms.forEach(term => {
          if (typeof term === 'string') {
            brandNamesSet.add(term.toLowerCase());
          }
        });
        console.log(`Found ${brandNamesSet.size} brand names for ${companyName}`);
      }
    } else {
      console.warn(`No Brand Names data found for ${companyName}`);
    }
    
    return brandNamesSet;
  } catch (error) {
    console.error('Error fetching Brand Names:', error);
    return new Set<string>();
  }
};

export const fetchDispositionProbabilityForWord = async (
  companyName: string,
  asin: string,
  term: string
): Promise<number | undefined> => {
  try {
    const termDocRef = doc(db, 'pmt3_disposition_ledger', companyName, asin, term);
    const termSnap = await getDoc(termDocRef);
    
    if (termSnap.exists()) {
      const data = termSnap.data();
      if (isDispositionLedgerData(data)) {
        if (data.cst === 'WORD' && typeof data.percentProbability === 'number') {
          return data.percentProbability;
        }
      }
    }
    
    return undefined;
  } catch (error) {
    console.error(`Error fetching probability for term "${term}":`, error);
    return undefined;
  }
};

const calculateKilledInClicks = (
  clicks: number,
  probability: number | undefined,
  wordClickThreshold: number
): number => {
  if (probability === undefined) {
    return wordClickThreshold - clicks;
  }
  
  const maxClicks = probability * wordClickThreshold;
  return Math.round(maxClicks - clicks);
};

export const fetchStillActiveWords = async (
  companyName: string,
  asin: string
): Promise<StillActiveWordsSummary> => {
  try {
    console.log(`Fetching Still Active Words for: ${companyName}/${asin}`);
    
    const [wordClickThreshold, brandNamesSet] = await Promise.all([
      fetchWordClickThreshold(companyName, asin),
      fetchBrandNamesSet(companyName)
    ]);
    
    const activeWordsArray: StillActiveWordData[] = [];
    
    try {
      const dataDocRef = doc(db, 'pmt3_all_word_results', companyName, 'data', asin);
      const docSnap = await getDoc(dataDocRef);
      
      if (!docSnap.exists()) {
        console.warn(`No All Word Results data found for ${companyName}/${asin}`);
        return {
          totalActiveWords: 0,
          wordClickThreshold,
          activeWords: []
        };
      }
      
      const data = docSnap.data();
      
      if (isAllWordResultsData(data) && Array.isArray(data.wordResultEntries)) {
        for (const entry of data.wordResultEntries) {
          const term = entry.term?.trim() || '';
          const termLower = term.toLowerCase();
          
          if (term === 'Invalid' || term === 'Keyword' || term === '') {
            continue;
          }
          
          if (entry.stillActive !== 'Yes') {
            continue;
          }
          
          const sales = parseNumericValue(entry.sales);
          if (sales > 0) {
            continue;
          }
          
          if (brandNamesSet.has(termLower)) {
            continue;
          }
          
          const clicks = parseNumericValue(entry.clicks);
          
          activeWordsArray.push({
            term,
            clicks,
            probability: undefined,
            killedInClicks: undefined
          });
        }
      }
      
      activeWordsArray.sort((a, b) => b.clicks - a.clicks);
      
      const top190 = activeWordsArray.slice(0, 190);
      
      for (const word of top190) {
        const probability = await fetchDispositionProbabilityForWord(companyName, asin, word.term);
        word.probability = probability;
        word.killedInClicks = calculateKilledInClicks(word.clicks, probability, wordClickThreshold);
      }
      
      console.log(`Found ${activeWordsArray.length} still active words, displaying top ${top190.length}`);
      
      return {
        totalActiveWords: activeWordsArray.length,
        wordClickThreshold,
        activeWords: top190
      };
      
    } catch (queryError) {
      console.error('Error querying Still Active Words:', queryError);
      return {
        totalActiveWords: 0,
        wordClickThreshold,
        activeWords: []
      };
    }
    
  } catch (error) {
    console.error('Error fetching Still Active Words:', error);
    throw error;
  }
};

export const getAvailableCompaniesForStillActiveWords = async (): Promise<string[]> => {
  try {
    const collectionRef = collection(db, 'pmt3_all_word_results');
    const querySnapshot = await getDocs(collectionRef);
    
    const companies: string[] = [];
    querySnapshot.forEach(doc => {
      companies.push(doc.id);
    });
    
    return companies.sort();
  } catch (error) {
    console.error('Error fetching available companies for still active words:', error);
    return [];
  }
};